import type { ParseResult } from "./parse-requirements";
import type { SchemaId } from "./schemas";
import type { AdaptiveFormProps } from "@kotaio/adaptive-form/react";

import { AdaptiveForm, AdaptiveFormProvider } from "@kotaio/adaptive-form/react";
import { AlertCircle, Braces, ChevronDown, Eye, EyeOff } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { Drawer } from "vaul";

import { KotaSymbolAndWordmarkLogo } from "~/assets/kota-symbol-and-wordmark";
import {
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "~/ui";

import { DEFAULT_REQUIREMENTS_JSON } from "./default-requirements";
import { JsonEditor } from "./json-editor";
import { parseAndValidateRequirements } from "./parse-requirements";
import { playgroundFieldComponents } from "./playground-field-components";
import { EXAMPLE_SCHEMAS, SCHEMA_OPTIONS } from "./schemas";

type FormData = NonNullable<AdaptiveFormProps["value"]>;

function TemplateSelector({
  value,
  onChange,
}: {
  value: SchemaId | "";
  onChange: (id: SchemaId | "") => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Example schemas"
      className="flex items-center gap-1 rounded-lg border border-input bg-card p-1"
    >
      {SCHEMA_OPTIONS.map(({ id, label }) => {
        const shortLabel = label.split(" — ")[0];
        const isActive = value === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            data-testid={`schema-template-${id}`}
            aria-selected={isActive}
            onClick={() => onChange(id)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {shortLabel}
          </button>
        );
      })}
    </div>
  );
}

function MobileTemplateSelector({
  value,
  onChange,
}: {
  value: SchemaId | "";
  onChange: (id: SchemaId | "") => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (SCHEMA_OPTIONS.some(({ id }) => id === v)) onChange(v as SchemaId);
      }}
    >
      <SelectTrigger
        className="h-8 w-auto min-w-[140px] text-xs"
        data-testid="schema-template-select"
      >
        <SelectValue placeholder="Select template" />
      </SelectTrigger>
      <SelectContent>
        {SCHEMA_OPTIONS.map(({ id, label }) => {
          const shortLabel = label.split(" — ")[0];
          return (
            <SelectItem key={id} value={id} className="text-xs">
              {shortLabel}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function EditorPanel({
  rawJson,
  onEditorChange,
  parseResult,
}: {
  rawJson: string;
  onEditorChange: (value: string) => void;
  parseResult: ParseResult;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
          Requirements JSON
        </span>
        {parseResult.success && (
          <span className="flex items-center gap-1 text-xs text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            Valid
          </span>
        )}
      </div>
      <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
        <JsonEditor
          value={rawJson}
          onChange={onEditorChange}
          className="min-h-[calc(100vh-280px)] md:min-h-[calc(100vh-220px)]"
        />
      </div>
      {!parseResult.success && (
        <div
          className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="text-xs leading-relaxed">{parseResult.error}</span>
        </div>
      )}
    </div>
  );
}

function FormValuesDrawer({
  formData,
  isOpen,
  onOpenChange,
}: {
  formData: FormData;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Drawer.Root open={isOpen} onOpenChange={onOpenChange}>
      <Drawer.Trigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="fixed right-4 bottom-4 z-20 gap-1.5 rounded-full shadow-lg md:hidden"
          aria-label="View form values"
        >
          <Braces className="h-3.5 w-3.5" />
          Values
        </Button>
      </Drawer.Trigger>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[80vh] flex-col rounded-t-xl border border-border/60 bg-card">
          <Drawer.Handle className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/30" />
          <Drawer.Title className="px-4 pt-3 pb-2 text-xs font-medium tracking-wider text-muted-foreground uppercase">
            Current form values
          </Drawer.Title>
          <Drawer.Description className="sr-only">
            JSON representation of current form field values
          </Drawer.Description>
          <div className="overflow-auto px-4 pb-6">
            <pre
              data-testid="form-values-mobile"
              className="overflow-auto rounded-lg bg-surface p-4 font-mono text-xs leading-relaxed text-foreground"
            >
              {JSON.stringify(formData, null, 2)}
            </pre>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function InlineFormValues({
  formData,
  isOpen,
  onToggle,
}: {
  formData: FormData;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="mt-8 border-t border-border/60 pt-6">
      <button
        type="button"
        data-testid="form-values-toggle"
        onClick={onToggle}
        className="flex w-full items-center gap-2 text-xs font-medium tracking-wider text-muted-foreground uppercase transition-colors hover:text-foreground"
      >
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
        Current form values
      </button>
      {isOpen && (
        <pre
          data-testid="form-values"
          className="mt-3 overflow-auto rounded-lg bg-surface p-4 font-mono text-xs leading-relaxed text-foreground"
        >
          {JSON.stringify(formData, null, 2)}
        </pre>
      )}
    </div>
  );
}

function FormPreviewContent({
  parseResult,
  formData,
  onFormDataChange,
  isValuesOpen,
  onToggleValues,
}: {
  parseResult: ParseResult;
  formData: FormData;
  onFormDataChange: (data: FormData) => void;
  isValuesOpen: boolean;
  onToggleValues: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
        Form Preview
      </span>
      {parseResult.success ? (
        <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm md:p-8">
          <AdaptiveFormProvider
            key={parseResult.requirements.id}
            requirements={parseResult.requirements}
          >
            <AdaptiveForm
              value={formData}
              onChange={onFormDataChange}
              components={playgroundFieldComponents}
              className="flex flex-col"
            />
          </AdaptiveFormProvider>

          {/* Desktop-only inline form values */}
          <div className="hidden md:block">
            <InlineFormValues formData={formData} isOpen={isValuesOpen} onToggle={onToggleValues} />
          </div>
        </div>
      ) : (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 p-4 md:min-h-[300px] md:p-8">
          <AlertCircle className="h-8 w-8 text-muted-foreground/40" />
          <div className="text-center">
            <p className="text-sm font-medium text-muted-foreground">Invalid schema</p>
            <p className="mt-1 text-xs text-muted-foreground/70">
              Fix the JSON to see the form preview
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PlaygroundDynamicFormRoute() {
  const [rawJson, setRawJson] = useState(DEFAULT_REQUIREMENTS_JSON);
  const [formData, setFormData] = useState<FormData>({});
  const [selectedTemplate, setSelectedTemplate] = useState<SchemaId | "">("simple");
  const [isEditorOpen, setIsEditorOpen] = useState(true);
  const [isValuesOpen, setIsValuesOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const parseResult = useMemo(() => parseAndValidateRequirements(rawJson), [rawJson]);

  const handleEditorChange = useCallback((value: string) => {
    setRawJson(value);
    setSelectedTemplate("");
  }, []);

  const handleTemplateSelect = useCallback((id: SchemaId | "") => {
    setSelectedTemplate(id);
    if (id) {
      setRawJson(JSON.stringify(EXAMPLE_SCHEMAS[id], null, 2));
      setFormData({});
    }
  }, []);

  const editorPanel = (
    <EditorPanel rawJson={rawJson} onEditorChange={handleEditorChange} parseResult={parseResult} />
  );

  const formPreviewPanel = (
    <FormPreviewContent
      parseResult={parseResult}
      formData={formData}
      onFormDataChange={setFormData}
      isValuesOpen={isValuesOpen}
      onToggleValues={() => setIsValuesOpen(!isValuesOpen)}
    />
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2.5">
              <KotaSymbolAndWordmarkLogo className="h-5" />
              <div className="hidden h-5 w-px bg-border md:block" />
              <h1 className="hidden text-sm font-semibold tracking-tight text-foreground-header md:block">
                AdaptiveForm Playground
              </h1>
            </div>

            {/* Mobile: Select dropdown */}
            <div className="md:hidden">
              <MobileTemplateSelector value={selectedTemplate} onChange={handleTemplateSelect} />
            </div>

            {/* Desktop: Button group */}
            <div className="hidden items-center gap-4 md:flex">
              <div className="h-5 w-px bg-border" />
              <TemplateSelector value={selectedTemplate} onChange={handleTemplateSelect} />
            </div>
          </div>

          {/* Hide/Show Editor button — desktop only */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditorOpen(!isEditorOpen)}
            aria-expanded={isEditorOpen}
            aria-label={isEditorOpen ? "Hide JSON editor" : "Show JSON editor"}
            className="hidden gap-1.5 text-xs md:inline-flex"
          >
            {isEditorOpen ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                Hide Editor
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                Show Editor
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-[1600px] px-4 py-4 md:px-6 md:py-6">
        {/* Mobile: Tab-based layout */}
        <div className="md:hidden">
          <Tabs defaultValue="editor">
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="editor" className="flex-1">
                Editor
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex-1">
                Preview
              </TabsTrigger>
            </TabsList>
            <TabsContent value="editor">{editorPanel}</TabsContent>
            <TabsContent value="preview">{formPreviewPanel}</TabsContent>
          </Tabs>
        </div>

        {/* Desktop: Grid layout */}
        <div
          data-testid="desktop-layout"
          className={cn(
            "hidden md:grid",
            "gap-4 md:gap-6",
            isEditorOpen ? "md:grid-cols-[1fr_1fr]" : "mx-auto max-w-2xl",
          )}
        >
          <Collapsible open={isEditorOpen} onOpenChange={setIsEditorOpen}>
            <CollapsibleContent>{editorPanel}</CollapsibleContent>
          </Collapsible>
          {formPreviewPanel}
        </div>
      </main>

      {/* Mobile-only: Form values drawer */}
      <FormValuesDrawer formData={formData} isOpen={isDrawerOpen} onOpenChange={setIsDrawerOpen} />
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex min-h-full items-center justify-center p-8">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <h1 className="text-2xl font-semibold">
            {error.status} {error.statusText}
          </h1>
          <p className="text-muted-foreground">{error.data}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center p-8">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="text-muted-foreground">
          {error instanceof Error ? error.message : "An unexpected error occurred."}
        </p>
      </div>
    </div>
  );
}
